import {PlusIcon} from '@heroicons/react/solid';
import type {ChangeEvent, ReactElement} from 'react';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import Button from '@/components/common/Button';
import {Input} from '@/components/common/Input';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import BlockUser from '@/screens/User/Details/BlockUser';
import type {UserDetailsReturnProps} from '@/screens/User/Details/types';

type UserSecurityNoteFormProps = Pick<
  UserDetailsReturnProps,
  'addSecurityNote' | 'isAddingSecurityNote' | 'user'
>;

const UserSecurityNoteForm = ({
  addSecurityNote,
  isAddingSecurityNote,
  user,
}: UserSecurityNoteFormProps): ReactElement => {
  const [securityNoteInput, setSecurityNoteInput] = useState<string>('');

  const handleSecurityNoteInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setSecurityNoteInput(event.target.value);
  };

  // `event.key` rather than the deprecated `event.keyCode === 13`; both cover
  // Enter and NumpadEnter, so the behaviour is unchanged.
  function onEnterKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      sendSecurityNote();
    }
  }

  const sendSecurityNote = () => {
    // Fire-and-forget: `addSecurityNote` catches and toasts its own failures,
    // and the input is cleared on a timer either way, so this only stops an
    // unexpected throw becoming an unhandled rejection.
    addSecurityNote(securityNoteInput).catch(() => {});
    setTimeout(() => {
      setSecurityNoteInput('');
    }, 1500);
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-200">
        Security Notes
      </h3>

      <div className="flex flex-col md:flex-row justify-between gap-4">
        <PermissionsProvider
          permission={AfriexAdminPermission.SecurityNoteCreate}
        >
          <div className="flex flex-nowrap w-full sm:w-auto">
            <Input
              onChange={handleSecurityNoteInputChange}
              onKeyUp={onEnterKey}
              name="securityNote"
              type="text"
              value={securityNoteInput}
              className="rounded border border-gray-300 border-r-0 text-gray-900 focus:ring-indigo-500 block flex-1 min-w-0 w-full sm:!w-[400px] sm:text-sm p-2.5 rounded-tr-none rounded-br-none dark:!bg-dark-inputBg"
              placeholder="Add Note"
            />
            <Button
              onClick={sendSecurityNote}
              colorScheme="cyan"
              variant="ghost"
              className="px-3 dark:bg-cyan-700 dark:text-gray-100 dark:border-dark-borderHeavy inline-flex items-center rounded-tl-none rounded-bl-none border whitespace-nowrap"
              disabled={!securityNoteInput || isAddingSecurityNote}
            >
              {isAddingSecurityNote ? (
                'Adding...'
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-1" /> Add
                </>
              )}
            </Button>
          </div>
        </PermissionsProvider>

        <PermissionsProvider permission={AfriexAdminPermission.UserBlock}>
          <BlockUser user={user} />
        </PermissionsProvider>
      </div>
    </div>
  );
};

export default UserSecurityNoteForm;
